<template>
  <v-row class="mt-10">
    <v-col cols="12" md="6" class="py-0">
      <v-switch
        dark
        hide-details
        color="yellow"
        v-model="showDisabledUsers"
        label="Mostrar usuários desativados"></v-switch>
    </v-col>
    <v-col cols="12" md="6" class="py-0">
      <v-text-field
        dark
        single-line
        hide-details
        color="yellow"
        label="Pesquisar"
        append-icon="mdi-magnify"
        v-model="search"></v-text-field>
    </v-col>
    <v-col cols="12" class="pt-0">
      <v-data-table
        dark
        sort-by="name"
        class="elevation-2 mt-4"
        :items="usersTable"
        :search="search"
        :headers="headers"
        :items-per-page="10">
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-if="loadingTable" class="no-hover text-center">
              <td :colspan="headers.length">
                <v-progress-circular
                  indeterminate
                  class="ma-5"
                  color="yellow"
                  :size="50"></v-progress-circular>
              </td>
            </tr>
            <tr @click="openUserDialog(item)" v-for="item in items" :key="item.id" class="tr-click">
              <td class="pr-0">
                <v-badge
                  top
                  dot
                  bordered
                  offset-x="10"
                  offset-y="10"
                  :color="item.active ? 'green' : 'red'">
                  <v-avatar size="36">
                    <img v-if="item.profile_photo" :src="item.profile_photo.url" />
                    <img v-else src="@/assets/images/no-photo.jpg" />
                  </v-avatar>
                </v-badge>
              </td>
              <td>{{ item.name }}</td>
              <td class="d-flex align-center">
                <img class="mr-2" :src="setEmailIcon(item.email)" />
                {{ item.email }}
              </td>
              <td>{{ formatDateTimeUTC(item.current_login_at) }}</td>
              <td>{{ formatDateTimeUTC(item.last_login_at) }}</td>
              <td v-if="item._2fa">
                <v-icon color="#69F0AE">mdi-check</v-icon>
              </td>
              <td v-else>
                <v-icon color="#FF5722">mdi-close</v-icon>
              </td>
              <td>
                {{ item.last_login_ip ? item.last_login_ip : "-- vazio --" }}
              </td>
              <td class="text-center">
                {{ item.roles.length }}
              </td>
              <td width="1%">
                <v-menu left dark bottom rounded="0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item link @click.stop="confirmAction(item, 'expirePassword', 'Tem certeza que deseja expirar a senha deste usuário?')">
                      <v-list-item-title>Expirar senha</v-list-item-title>
                    </v-list-item>
                    <v-list-item link @click.stop="confirmAction(item, 'toggle2Fa', item._2fa ? 'Tem certeza que deseja DESATIVAR o 2FA deste usuário?' : 'Tem certeza que deseja ATIVAR o 2FA deste usuário?')">
                      <v-list-item-title>Revogar/Ativar 2FA</v-list-item-title>
                    </v-list-item>
                    <v-list-item link @click.stop="confirmAction(item, 'resetPassword', 'Tem certeza que deseja enviar e-mail de recuperação de senha?')">
                      <v-list-item-title>Enviar recuperação de senha</v-list-item-title>
                    </v-list-item>
                    <v-list-item :to="{ name: 'logs', query: { executer_id: item.id } }">
                      <v-list-item-title>Ver os logs</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
            <tr class="no-hover text-center" v-if="!loadingTable && !items.length">
              <td :colspan="headers.length">
                <p class="mt-4">Nenhuma usuário encontrada</p>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-col>
    <v-btn fab right bottom absolute color="yellow" @click="openUserDialog({}, true)">
      <v-icon> mdi-plus </v-icon>
    </v-btn>

    <UserDialog
      :key="currentUser.id"
      :user="currentUser"
      :dialog="dialog"
      :allRoles="allRoles"
      :allProfiles="allProfiles"
      :createForm="createForm"
      :minDate="minDate"
      @close="closeUserDialog"
      @saved="updateUser"
      @snackbar="$emit('snackbar', $event)" />
      
    <v-dialog v-model="dialogConfirm" max-width="400" dark>
      <v-card>
        <v-card-title class="text-h5">Confirmação</v-card-title>
        <v-card-text class="mt-2">{{ confirmText }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogConfirm = false">Cancelar</v-btn>
          <v-btn color="yellow" text @click="executeConfirmedAction">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script lang="ts">
import { mapActions } from "vuex";
import { Vue, Component } from "vue-property-decorator";
import { orderByName } from "@/utils/sort";
import { getRoles } from "@/services/roles";
import { getProfiles } from "@/services/profiles";
import { expirePassword, resetPassword, twoFA } from "@/services/user";
import { formatDateTime, formatDateTimeUTC} from "@/utils/date";

import UserDialog from "./dialogs/UserDialog.vue";

@Component({
  components: {
    UserDialog,
  },
  methods: {
    ...mapActions("usersState", ["fetchUsers"]),
    formatDateTime,
    formatDateTimeUTC,
  },
})
export default class Users extends Vue {
  fetchUsers!: () => Promise<any>;

  get usersTable() {
    return this.users.filter((u) => {
      if (this.showDisabledUsers) return true;
      return u.active;
    });
  }
  // Dias para a expiração da senha
  PASSWORD_EXPIRATION_DAYS = 60;

  search: any = "";
  minDate: any = "";
  createForm = false;
  showDisabledUsers = false;
  emailRules = [
    (v: any): any => !!v || "E-mail deve ser informado",
    (v: any): any => /.+@.+/.test(v) || "E-mail inválido | ex: pessoa@domínio.com",
  ];
  allRoles: any = [];
  allProfiles: any = [];
  dialog = false;
  loadingTable = true;
  users = [] as Array<any>;
  loading = false;
  headers = [
    { text: "", value: "name", align: "center", width: "1%", sortable: false },
    { text: "Nome", value: "name", align: "start", width: "30%" },
    { text: "E-mail", value: "email", align: "start" },
    { text: "Ult. Login", value: "current_login_at", align: "start", width: "15%" },
    { text: "Pen. Login", value: "last_login_at", align: "start", width: "15%" },
    { text: "2FA", value: "_2fa", align: "start", width: "15%" },
    { text: "IP", value: "last_login_ip", align: "start", width: "10%" },
    { text: "Permissões", value: "", align: "start", width: "1%", sortable: false },
    { align: "center", width: "3%", sortable: false },
  ];

  /*Variáveis para controle do modal de confirmação */
  dialogConfirm = false;
  confirmText = "";
  pendingActionItem: any = null;
  pendingActionType = "";

  /* Variáveis do formulário */
  currentUser = {
    id: "",
    name: "",
    email: "",
    active: false,
    force_login: false,
    password_due_date: "",
    _2fa: false,
    roles: [],
    profiles: [],
  };

  get getItemsTable(): any {
    return this.users.filter((u) => {
      if (this.showDisabledUsers) return true;
      return u.active;
    });
  }

  updateUser(user: any) {
    if (!user || !user.id) return;

    const idx = this.users.findIndex((u) => u.id === user.id);
    if (idx !== -1) {
      this.$set(this.users, idx, { ...this.users[idx], ...user });
    } else {
      this.users.push(user);
    }
  }

  async mounted(): Promise<void> {
    this.minDate = this.parseDate(this.formatDate(new Date().toISOString()));

    try {
      const resUsers = await this.fetchUsers();
      if (resUsers) {
        this.users = resUsers;
      }
    } catch (err) {
      console.error(err);
      this.$emit("snackbar", {
        message: "Erro ao carregar os usuários!",
      });
    } finally {
      this.loadingTable = false;
    }

    try {
      const resRoles = await getRoles(["id", "name", "app"]);
      this.allRoles = resRoles.data;
      this.allRoles.map((item: any) => {
        item.name = item.app === null ? "Auth - " + item.name : item.app.name + " - " + item.name;
      });
      this.allRoles = orderByName(this.allRoles);
    } catch (err) {
      console.error(err);
      this.$emit("snackbar", {
        message: "Erro ao carregar as permissões!",
      });
    }

    try {
      const resProfiles = await getProfiles();
      this.allProfiles = resProfiles.data;
    } catch (err) {
      console.error(err);
      this.$emit("snackbar", {
        message: "Erro ao carregar os perfis!",
      });
    }

    if (Object.keys(this.$route.query).length !== 0) {
      let id: any = atob(this.$route.query.id + "");
      this.openUserDialog(this.users.find((u: any) => u.id == id));
    }
  }

  /* Métodos para controlar a confirmação */
  confirmAction(item: any, type: string, message: string) {
    this.pendingActionItem = item;
    this.pendingActionType = type;
    this.confirmText = message;
    this.dialogConfirm = true;
  }

  async executeConfirmedAction() {
    this.dialogConfirm = false;
    if (!this.pendingActionItem) return;

    if (this.pendingActionType === 'expirePassword') {
        await this.expirePassword(this.pendingActionItem);
    } else if (this.pendingActionType === 'toggle2Fa') {
        await this.toggle2Fa(this.pendingActionItem);
    } else if (this.pendingActionType === 'resetPassword') {
        await this.resetPassword(this.pendingActionItem);
    }
    
    this.pendingActionItem = null;
    this.pendingActionType = "";
  }

  async expirePassword(item?: any) {
    if (!item || !item.id) return;
    try {
      await expirePassword(item.id);
      this.$emit("snackbar", { message: "Senha foi expirada!" });
      this.updateUser(item);
    } catch (error) {
      this.$emit("snackbar", { message: "Erro ao expirar senha!" });
    }
  }

  async resetPassword(item?: any) {
    if (!item || !item.id) return;

    try {
      await resetPassword(item.email);
      this.$emit("snackbar", { message: "Reset de senha foi enviado!" });
      this.updateUser(item);
    } catch (error) {
      this.$emit("snackbar", { message: "Erro ao enviar reset de senha!" });
    }
  }

  async toggle2Fa(item?: any) {
    if (!item || !item.id) return;
    try {
      const response: any = await twoFA(item.id);
      // Atualiza visualmente se o back retornar o novo estado
      if (response && response.data) {
          item._2fa = response.data._2fa !== null;
      }
      this.$emit("snackbar", {
        message: `2FA foi ${item._2fa ? "ativado" : "desativado"}!`,
      });
      this.updateUser(item);
    } catch (error) {
      this.$emit("snackbar", { message: "Erro ao enviar reset de senha!" });
    }
  }

  setEmailIcon(email: string): string {
    const emailDomain = email.split("@")[1];
    return `https://www.google.com/s2/favicons?domain=${emailDomain}`;
  }

  openUserDialog(item: any, createModal = false): void {
    this.dialog = true;
    this.createForm = createModal;
    this.currentUser = { ...item };
  }

  closeUserDialog(): void {
    if (this.$route.fullPath != this.$route.path) this.setRouteQuery();
    this.dialog = false;
  }

  formatDate(date: any): string {
    if (!date) return "";

    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  }

  parseDate(date: any): string {
    if (!date) return "";

    const [day, month, year] = date.split("/");
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  addfilters(id: any): void {
    if (this.$route.fullPath == this.$route.path) {
      let query: any = {
        id: btoa(id),
      };
      this.setRouteQuery(query);
    }
  }

  setRouteQuery(query: any = {}): void {
    let name: any = this.$route.name;
    this.$router.replace({
      name: name,
      query: query,
    });
  }
}
</script>
<style lang="scss" scoped>
.v-list {
  background: #1e1e1e !important;
}
</style>
